import { z } from "zod";
import type { ZodError } from "zod";

// Derive issue type from ZodError — avoids the deprecated ZodIssue named export in Zod v4
type ZodIssue = ZodError["issues"][number];

// ── Guard — active in dev (pnpm dev) and staging builds only
// import.meta.env.DEV is Vite's built-in: true only on pnpm dev
// VITE_APP_ENV=staging catches staging builds (pnpm build --mode staging)
const isLoggingEnabled =
  import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "staging";

// ── Formatters

const fmt = {
  ns: (namespace: string) => `%c[${namespace}]`,
  nsStyle: "color: #6366f1; font-weight: 600;",

  method: (method: string) => {
    const colors: Record<string, string> = {
      GET: "#22c55e",
      POST: "#3b82f6",
      PUT: "#f59e0b",
      PATCH: "#f59e0b",
      DELETE: "#ef4444",
    };
    return {
      label: `%c${method}`,
      style: `color: ${colors[method.toUpperCase()] ?? "#94a3b8"}; font-weight: 700;`,
    };
  },

  status: (status: number) => {
    if (status >= 500)
      return { label: `%c${status}`, style: "color: #ef4444; font-weight: 700;" };
    if (status >= 400)
      return { label: `%c${status}`, style: "color: #f59e0b; font-weight: 700;" };
    if (status >= 300)
      return { label: `%c${status}`, style: "color: #a78bfa; font-weight: 700;" };
    return { label: `%c${status}`, style: "color: #22c55e; font-weight: 700;" };
  },
};

// ── Zod issue renderer
// Shows: [3].owner.name | expected: string | received: null
const formatZodIssues = (issues: ZodIssue[]): void => {
  for (const issue of issues) {
    const path = issue.path.length
      ? issue.path
          .map((p) => (typeof p === "number" ? `[${p}]` : `.${String(p)}`))
          .join("")
      : "(root)";

    const received =
      "received" in issue
        ? String((issue as { received?: unknown }).received)
        : issue.code;

    console.error(
      `  %cpath:%c ${path}   %cexpected:%c ${issue.message}   %creceived:%c ${received}`,
      "color:#94a3b8",
      "color:#f1f5f9",
      "color:#94a3b8",
      "color:#f1f5f9",
      "color:#94a3b8",
      "color:#ef4444; font-weight:600",
    );
  }
};

// ── Logger factory

export const createLogger = (namespace: string) => {
  const logZodIssues = (issues: ZodIssue[], context?: string): void => {
    if (!isLoggingEnabled) return;
    console.group(
      `${fmt.ns(namespace)} %c✖ Zod validation failed${context ? ` — ${context}` : ""}`,
      fmt.nsStyle,
      "color: #ef4444; font-weight: 600;",
    );
    formatZodIssues(issues);
    console.groupEnd();
  };

  return {
    log: (...args: unknown[]): void => {
      if (!isLoggingEnabled) return;
      console.log(fmt.ns(namespace), fmt.nsStyle, ...args);
    },

    info: (...args: unknown[]): void => {
      if (!isLoggingEnabled) return;
      console.info(fmt.ns(namespace), fmt.nsStyle, ...args);
    },

    warn: (...args: unknown[]): void => {
      if (!isLoggingEnabled) return;
      console.warn(fmt.ns(namespace), fmt.nsStyle, ...args);
    },

    error: (...args: unknown[]): void => {
      if (!isLoggingEnabled) return;
      console.error(fmt.ns(namespace), fmt.nsStyle, ...args);
      // Future: Sentry.captureException(...args)
    },

    // ── Zod parse failure — single entity (handleMappedResponse)
    zod: logZodIssues,

    // ── Zod per-item failure (handlePaginatedResponse)
    // Shows which index was skipped and why, with the raw item collapsed
    zodItem: (index: number, issues: ZodIssue[], rawItem?: unknown): void => {
      if (!isLoggingEnabled) return;
      console.groupCollapsed(
        `${fmt.ns(namespace)} %c✖ Item[${index}] skipped — Zod failed (${issues.length} issue${issues.length > 1 ? "s" : ""})`,
        fmt.nsStyle,
        "color: #f59e0b; font-weight: 600;",
      );
      formatZodIssues(issues);
      if (rawItem !== undefined) {
        console.groupCollapsed("%craw data", "color:#94a3b8");
        console.log(rawItem);
        console.groupEnd();
      }
      console.groupEnd();
    },

    // ── Drop-in replacement for Schema.parse() in service mappers.
    // Logs the Zod error with full path/received info before re-throwing,
    // so React Query error state is still set but the issue is visible in DevTools.
    parseWith: <T>(schema: z.ZodType<T>, data: unknown, context?: string): T => {
      const parsed = schema.safeParse(data);
      if (!parsed.success) {
        logZodIssues(parsed.error.issues, context);
        throw parsed.error;
      }
      return parsed.data;
    },

    // ── HTTP request/response — used by axios interceptors
    http: {
      request: (method: string, url: string, params?: unknown): void => {
        if (!isLoggingEnabled) return;
        const m = fmt.method(method);
        console.groupCollapsed(
          `${fmt.ns(namespace)} ${m.label} %c${url}`,
          fmt.nsStyle,
          m.style,
          "color: #cbd5e1;",
        );
        if (params) {
          console.log("%cparams", "color:#94a3b8", params);
        }
        console.groupEnd();
      },

      response: (
        method: string,
        url: string,
        status: number,
        durationMs: number,
      ): void => {
        if (!isLoggingEnabled) return;
        const m = fmt.method(method);
        const s = fmt.status(status);
        const isError = status >= 400;
        const groupFn = isError ? console.group : console.groupCollapsed;
        groupFn(
          `${fmt.ns(namespace)} ${m.label} %c${url} → ${s.label} %c(${durationMs}ms)`,
          fmt.nsStyle,
          m.style,
          "color: #cbd5e1;",
          s.style,
          "color: #64748b;",
        );
        console.groupEnd();
      },

      error: (
        method: string,
        url: string,
        status: number | null,
        body?: unknown,
      ): void => {
        if (!isLoggingEnabled) return;
        const m = fmt.method(method);
        const statusLabel = status !== null ? String(status) : "Network Error";
        console.group(
          `${fmt.ns(namespace)} ${m.label} %c${url} → %c✖ ${statusLabel}`,
          fmt.nsStyle,
          m.style,
          "color: #cbd5e1;",
          "color: #ef4444; font-weight: 600;",
        );
        if (body !== undefined) {
          console.error("%cresponse body", "color:#94a3b8", body);
        }
        console.groupEnd();
      },
    },
  };
};

// ── Core instance — used inside core/libs
export const logger = createLogger("core");
